import { useMemo } from "react"
import {
  Avatar,
  Box,
  Button,
  Typography,
  useTheme,
  Card,
  ButtonProps,
  CardProps,
} from "@mui/material"
import size from "lodash/size"
import { getIcon, IConfigIcon } from "utils/icon"
import { useTranslation } from "providers/i18n"
import { useMobileUrl } from "hooks/useMobileUrl"
import { useImageModal } from "hooks/imageModal/useImageModal"
import { FeatureLabel } from "./FeatureLabel"
import { CloseIconButton } from "components/Button/CloseIconButton"
import { QRCode } from "components/QRCode"
import { ComingSoonChip, NewShopChip } from "components/chips"
import {
  BulletedListIcon,
  ClockIcon,
  CloseIcon,
  LocationIcon,
} from "../../icons"
import { StoreIcon } from "components/icons"
import { Image, ImageCarouselModal } from "components/Image"
import { Carousel, CarouselItem } from "components/Carousel"
import { GetFeatureDetailResult } from "utils/feature"

export type FeatureCardData = GetFeatureDetailResult & { [k: string]: any }

export interface IStandardFeatureCardProps {
  open?: boolean
  loading?: boolean
  isNavigatable?: boolean
  data: FeatureCardData
  onClose: () => void
  onNavigate?: () => void
  onClickPromotionButton?: () => void
  directionButtonProps?: ButtonProps
  promotionButtonProps?: ButtonProps
  categoryIconProp?: IConfigIcon
  childrenProps?: CardProps
}

export const StandardFeatureCard: React.FC<IStandardFeatureCardProps> = ({
  open = false,
  onClose,
  directionButtonProps = {},
  promotionButtonProps = {},
  loading = true,
  data = {},
  onNavigate = () => {},
  onClickPromotionButton = () => {},
  categoryIconProp = {},
  childrenProps = {},
  isNavigatable = true,
  ...props
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { mobileUrl } = useMobileUrl()
  const {
    logo,
    category,
    name,
    hours,
    gallery,
    description,
    location,
    promotions,
    isNew,
    isComingSoon,
  } = data as FeatureCardData
  const galleryImageLength = size(gallery)
  const galleryUrls = useMemo(
    () => (size(gallery) > 0 ? gallery?.map((v) => v?.url) : []),
    [gallery]
  )
  const { handleImage, openModal, activeImage, handleClose } = useImageModal()

  const styleOverrides = theme.components[`FeatureCard`]?.styleOverrides || {}
  const {
    root,
    primarySection,
    secondarySection,
    header,
    content,
    avatar,
    actionContainer,
    listContainer,
    qrCodeContainer,
    closeButton,
    carousel,
    operationTimeLabel,
    contentWrapper,
    imageSection,
    galleryImage,
    locationWrapper,
    locationContainer,
    categoryContainer,
    operationTimeContainer,
    labelContainer,
    statusTagContainer,
    newShopChip,
    comingSoonChip,
  } = styleOverrides

  const { galleryCarouselOption, showStatusChip = false } =
    theme.components[`FeatureCard`]?.defaultProps || {}

  const closeIcon = useMemo(
    () => getIcon(theme?.assets?.closeIcon) || <CloseIcon />,
    [theme]
  )

  const locationIcon = useMemo(
    () => getIcon(theme?.assets?.locationIcon) || <LocationIcon />,
    [theme]
  )

  const categoryIcon = useMemo(
    () => getIcon(theme?.assets?.listIcon) || <BulletedListIcon />,
    [theme]
  )

  const clockIcon = useMemo(
    () => getIcon(theme?.assets?.clockIcon) || <ClockIcon />,
    [theme]
  )

  const hasGallery = size(gallery) > 0

  return (
    <>
      <Card sx={root} {...childrenProps}>
        <CloseIconButton
          sx={closeButton}
          icon={closeIcon}
          onClick={onClose}
          size="large"
        />
        <Box
          className={hasGallery ? "content-with-gallery" : ""}
          sx={contentWrapper}
        >
          <Box sx={primarySection}>
            <Box sx={header}>
              <Avatar variant="circular-logo" sx={avatar} src={logo}>
                {getIcon(categoryIconProp, null, {
                  imageIconProps: {
                    solidColorFilterId: "placeDetail_avatar_filter",
                  },
                }) || <StoreIcon />}
              </Avatar>
              <Box sx={labelContainer}>
                {showStatusChip && (isNew || isComingSoon) && (
                  <Box sx={statusTagContainer}>
                    {isNew && <NewShopChip sx={newShopChip} />}
                    {isComingSoon && (
                      <ComingSoonChip
                        sx={comingSoonChip}
                        label={t("Coming soon")}
                      />
                    )}
                  </Box>
                )}
                <Typography gutterBottom variant="h5">
                  {t(name)}
                </Typography>
              </Box>
            </Box>
            {description && (
              <Box sx={content}>
                <Typography variant="body2" color="text.secondary">
                  {t(description)}
                </Typography>
              </Box>
            )}
          </Box>
          <Box sx={secondarySection}>
            <Box sx={listContainer}>
              <Box sx={locationWrapper}>
                <Box sx={locationContainer}>
                  {locationIcon}
                  <FeatureLabel label={t("Location")} value={t(location)} />
                </Box>
                <Box sx={categoryContainer}>
                  {categoryIcon}
                  <FeatureLabel label={t("Category")} value={t(category)} />
                </Box>
              </Box>
              <Box sx={operationTimeContainer}>
                {clockIcon}
                <FeatureLabel
                  label={t("Operation Time")}
                  value={hours || "-"}
                  sx={operationTimeLabel}
                />
              </Box>
              {mobileUrl && (
                <QRCode
                  sx={qrCodeContainer}
                  url={mobileUrl}
                  caption={t("Scan Here")}
                  secondaryCaption={t("For Mobile")}
                />
              )}
            </Box>
          </Box>
          <Box sx={imageSection}>
            {galleryImageLength > 0 && (
              <Box>
                <Carousel
                  options={{
                    trimSpace: false,
                    pagination: true,
                    perPage: 1,
                    focus: "center",
                    gap: 0,
                    width: 360,
                    height: 270,
                    padding: 0,
                    margin: 0,
                    autoplay: true,
                    arrows: true,
                  }}
                  sx={carousel}
                >
                  {gallery?.map((image, idx) => (
                    <CarouselItem key={`${image.name}-${idx}`}>
                      <Image
                        onClick={() => handleImage(image.url)}
                        src={image.url}
                        alt={`${image.name}-${idx}`}
                        sx={galleryImage}
                      />
                    </CarouselItem>
                  ))}
                </Carousel>
              </Box>
            )}
          </Box>
        </Box>
        <Box sx={actionContainer}>
          {promotions.length > 0 && (
            <Button
              variant="outlined"
              onClick={onClickPromotionButton}
              {...promotionButtonProps}
            >
              {t("Promotion")}
            </Button>
          )}
          <Button
            variant="contained"
            onClick={onNavigate}
            {...directionButtonProps}
            sx={{
              visibility: isNavigatable && !isComingSoon ? "visible" : "hidden",
            }}
          >
            {t("Get direction")}
          </Button>
        </Box>
      </Card>
      <ImageCarouselModal
        activeSrc={activeImage}
        open={openModal}
        imageSrcs={galleryUrls}
        onClose={handleClose}
        carouselOptions={galleryCarouselOption}
      />
    </>
  )
}
